import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  LogOut, GraduationCap, User, CalendarDays, Wallet, BookOpen, KeyRound,
  MessageSquareHeart, ClipboardList, Users, Loader2, ChevronDown,
  ChevronRight, AlertCircle,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { applyFonts } from "@/lib/landingFonts";
import { getPalette } from "@/lib/landing";
import InstallPwaPrompt from "@/components/InstallPwaPrompt";
import useSchoolManifest from "@/lib/useSchoolManifest";

/**
 * Parent Portal — top-level dashboard.
 * Pulls /api/parent/me/children for the child picker + visibility settings,
 * then /api/parent/students/{id} for the selected child's snapshot. Sections
 * are rendered only when the owner has enabled them in Parent Portal Settings.
 */
const SECTION_META = {
  profile:      { label: "Profile & Class",  icon: User,             tint: "bg-[#DBEAFE] text-[#2563EB]" },
  attendance:   { label: "Attendance",       icon: CalendarDays,     tint: "bg-[#DCFCE7] text-[#15803D]" },
  payments:     { label: "Payments",         icon: Wallet,           tint: "bg-[#FEF3C7] text-[#92400E]" },
  grades:       { label: "Grades",           icon: BookOpen,         tint: "bg-[#FAE8FF] text-[#7E22CE]" },
  feedback:     { label: "Teacher Feedback", icon: MessageSquareHeart, tint: "bg-[#FFE4E6] text-[#BE123C]" },
  report_card:  { label: "Report Card",      icon: ClipboardList,    tint: "bg-[#E0E7FF] text-[#3730A3]" },
  guardians:    { label: "Family",           icon: Users,            tint: "bg-[#FFE8D6] text-[#9A3412]" },
};

const SECTION_ORDER = ["profile", "attendance", "payments", "grades", "feedback", "report_card", "guardians"];

const peso = (n) => `₱${Number(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d.length === 10 ? d + "T00:00:00" : d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return d; }
};

export default function ParentDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [visibility, setVisibility] = useState({});
  const [school, setSchool] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snapLoading, setSnapLoading] = useState(false);
  const [error, setError] = useState("");

  // 1) Load child list + visibility + school brand
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/parent/me/children");
        if (!alive) return;
        setChildren(data.children || []);
        setVisibility(data.visibility || {});
        setSchool(data.school);
        if ((data.children || []).length > 0) setSelectedId(data.children[0].id);
        else setSnapshot(null);
      } catch (e) {
        if (alive) setError(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Apply the school's custom typography (heading / body / button) on the
  // parent portal. Reset on unmount so logging out doesn't leak fonts.
  useEffect(() => {
    if (!school) return;
    applyFonts({
      heading: school.font_heading || null,
      body: school.font_body || null,
      button: school.font_button || null,
    });
    return () => applyFonts({ heading: null, body: null, button: null });
  }, [school?.font_heading, school?.font_body, school?.font_button]);

  // 2) Load the selected child's snapshot whenever selection changes
  useEffect(() => {
    if (!selectedId) return;
    let alive = true;
    (async () => {
      try {
        setSnapLoading(true);
        const { data } = await api.get(`/parent/students/${selectedId}`);
        if (alive) setSnapshot(data);
      } catch (e) {
        if (alive) toast.error(formatApiError(e));
      } finally {
        if (alive) setSnapLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [selectedId]);

  const handleLogout = () => { logout(); navigate("/", { replace: true }); };

  // Per-school PWA — swaps the document manifest + theme-color to the school's
  // identity so installing produces a school-branded home-screen icon.
  const palette = useMemo(() => getPalette(school?.theme_color), [school?.theme_color]);
  useSchoolManifest(school?.slug || "", palette.text);

  // ---- Render ----
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading your child's info…
    </div>;
  }
  if (error) {
    return <div className="min-h-screen flex items-center justify-center text-[#C62828]">{error}</div>;
  }

  const visibleSections = SECTION_ORDER.filter((s) => visibility[s]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EFF6FF] via-white to-[#F5F3FF]" data-testid="parent-dashboard">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur border-b border-slate-200/60 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-2xl overflow-hidden bg-[#DBEAFE] flex items-center justify-center shrink-0">
              {school?.logo_base64
                ? <img src={school.logo_base64} alt="" className="w-full h-full object-cover" />
                : <GraduationCap className="w-5 h-5 text-[#2563EB]" />}
            </div>
            <div className="min-w-0">
              <p className="text-xs text-slate-400 font-medium">Parent Portal</p>
              <h1 className="font-heading text-lg font-bold truncate">{school?.name || "Your school"}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline-block text-sm text-slate-600 font-medium">{user?.name}</span>
            <Button variant="outline" onClick={() => navigate("/parent/change-password")}
              className="rounded-full h-9 px-3 text-sm" data-testid="parent-change-password-btn">
              <KeyRound className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Change password</span>
            </Button>
            <Button variant="outline" onClick={handleLogout}
              className="rounded-full h-9 px-3 text-sm" data-testid="parent-logout-btn">
              <LogOut className="w-4 h-4 mr-1" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-8">
        {/* Install PWA prompt — auto-hides when already installed / unsupported */}
        {school?.slug && (
          <div className="mb-6">
            <InstallPwaPrompt
              slug={school.slug}
              schoolName={school.public_display_name || school.name || "this school"}
              palette={palette}
            />
          </div>
        )}

        {/* Child picker */}
        {children.length > 1 && (
          <div className="mb-6">
            <p className="text-sm font-heading text-slate-500 mb-2">Choose a child</p>
            <div className="flex gap-3 flex-wrap" data-testid="parent-child-picker">
              {children.map((c) => (
                <button key={c.id} onClick={() => setSelectedId(c.id)}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-2xl border transition-all ${
                    c.id === selectedId
                      ? "bg-[#2563EB] text-white border-[#2563EB] shadow-floating"
                      : "bg-white border-slate-200 hover:border-[#2563EB]/40 hover:shadow-sm"
                  }`}
                  data-testid={`parent-child-${c.id}`}
                >
                  <span className="w-9 h-9 rounded-full overflow-hidden bg-white/30 flex items-center justify-center shrink-0">
                    {c.photo
                      ? <img src={c.photo} alt="" className="w-full h-full object-cover" />
                      : <User className={`w-5 h-5 ${c.id === selectedId ? "text-white" : "text-slate-500"}`} />}
                  </span>
                  <span className="text-sm font-heading font-semibold">{c.name} {c.last_name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {children.length === 0 && (
          <Card className="p-10 text-center rounded-3xl border-0 shadow-floating">
            <AlertCircle className="w-10 h-10 text-slate-400 mx-auto mb-3" />
            <h2 className="font-heading text-lg font-semibold">No children linked to this account yet</h2>
            <p className="text-sm text-slate-500 mt-1">Please contact your school to set up access for your child.</p>
          </Card>
        )}

        {/* Selected child hero */}
        {snapshot && (
          <Card className="p-6 rounded-3xl border-0 shadow-floating mb-6" data-testid="parent-child-hero">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-2xl overflow-hidden bg-[#DBEAFE] flex items-center justify-center shrink-0">
                {snapshot.photo
                  ? <img src={snapshot.photo} alt="" className="w-full h-full object-cover" />
                  : <User className="w-9 h-9 text-[#2563EB]" />}
              </div>
              <div className="min-w-0">
                <h2 className="font-heading text-2xl font-bold">{snapshot.name} {snapshot.last_name}</h2>
                {snapshot.profile && (
                  <p className="text-sm text-slate-500 mt-1">
                    {snapshot.profile.class_name && <>Class: <span className="font-semibold text-foreground">{snapshot.profile.class_name}</span></>}
                    {snapshot.profile.section_name && <> · Section: <span className="font-semibold text-foreground">{snapshot.profile.section_name}</span></>}
                    {!snapshot.profile.class_name && !snapshot.profile.section_name && <>Not yet enrolled in a class.</>}
                  </p>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Sections */}
        {snapLoading && (
          <div className="text-center text-slate-500 py-12 flex items-center justify-center">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading details…
          </div>
        )}

        {!snapLoading && snapshot && visibleSections.length === 0 && (
          <Card className="p-10 text-center rounded-3xl border-0 shadow-floating">
            <AlertCircle className="w-10 h-10 text-slate-400 mx-auto mb-3" />
            <p className="font-heading text-lg">Your school has not enabled any sections yet</p>
            <p className="text-sm text-slate-500 mt-1">Please check back later.</p>
          </Card>
        )}

        {!snapLoading && snapshot && visibleSections.map((key) => (
          <SectionPanel key={key} sectionKey={key} snapshot={snapshot} />
        ))}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SectionPanel — collapsible card per parent-portal section
// ---------------------------------------------------------------------------
function SectionPanel({ sectionKey, snapshot }) {
  const meta = SECTION_META[sectionKey];
  const [open, setOpen] = useState(true);
  const Icon = meta.icon;

  return (
    <Card className="rounded-3xl border-0 shadow-floating mb-4 overflow-hidden" data-testid={`parent-section-${sectionKey}`}>
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors">
        <div className="flex items-center gap-3">
          <span className={`w-9 h-9 rounded-2xl flex items-center justify-center ${meta.tint}`}>
            <Icon className="w-4 h-4" />
          </span>
          <span className="font-heading font-bold text-lg">{meta.label}</span>
        </div>
        {open ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
      </button>
      {open && <div className="px-6 pb-6">{renderSection(sectionKey, snapshot)}</div>}
    </Card>
  );
}

function renderSection(key, s) {
  if (key === "profile") {
    const p = s.profile || {};
    return (
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <Field label="Birthday" value={fmtDate(p.birthday)} />
        <Field label="Status" value={(p.status || "").replace(/^\w/, (c) => c.toUpperCase())} />
        <Field label="Address" value={[p.address, p.city, p.province, p.country].filter(Boolean).join(", ") || "—"} className="sm:col-span-2" />
        <Field label="Class" value={p.class_name || "—"} />
        <Field label="Section" value={p.section_name || "—"} />
      </dl>
    );
  }
  if (key === "attendance") {
    const sum = s.attendance?.summary || {};
    const recent = s.attendance?.recent || [];
    return (
      <div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <Stat label="Present" value={sum.present || 0} tint="bg-[#DCFCE7] text-[#15803D]" />
          <Stat label="Absent"  value={sum.absent || 0}  tint="bg-[#FEE2E2] text-[#B91C1C]" />
          <Stat label="Late"    value={sum.late || 0}    tint="bg-[#FEF3C7] text-[#92400E]" />
          <Stat label="Excused" value={sum.excused || 0} tint="bg-[#E0E7FF] text-[#3730A3]" />
        </div>
        {recent.length === 0
          ? <Empty>No attendance records yet.</Empty>
          : <Table rows={recent.map((r) => [fmtDate(r.date), (r.status || "").toUpperCase()])} headers={["Date", "Status"]} />}
      </div>
    );
  }
  if (key === "payments") {
    const p = s.payments || {};
    return (
      <div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
          <Stat label="Total paid" value={peso(p.total_paid)} tint="bg-[#DCFCE7] text-[#15803D]" />
          <Stat label="Tuition Fee" value={peso(p.tuition_fee)} tint="bg-[#FEF3C7] text-[#92400E]" />
          <Stat label="Discount" value={peso(p.discount)} tint="bg-[#E0E7FF] text-[#3730A3]" />
        </div>
        {(p.history || []).length === 0
          ? <Empty>No payments recorded yet.</Empty>
          : <Table
              headers={["Date", "Period", "Method", "Amount"]}
              rows={p.history.map((r) => [
                fmtDate(r.date), r.period_label || "—", r.method || "—", peso(r.amount),
              ])}
            />}
      </div>
    );
  }
  if (key === "grades") {
    const rows = s.grades || [];
    if (rows.length === 0) return <Empty>No grades posted yet.</Empty>;
    return (
      <Table
        headers={["Date", "Subject", "Type", "Score", "Period"]}
        rows={rows.map((r) => [
          fmtDate(r.date), r.subject || "—", r.assessment_type || "—",
          `${Number(r.score || 0)} / ${Number(r.max_score || 0)}`, r.period_label || "—",
        ])}
      />
    );
  }
  if (key === "feedback") {
    const rows = s.feedback || [];
    if (rows.length === 0) return <Empty>No feedback yet.</Empty>;
    return (
      <div className="space-y-3">
        {rows.map((f) => (
          <div key={f.id} className="border border-slate-200 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="font-heading font-semibold">{f.category || "Note"}</span>
              <span className="text-xs text-slate-400">{fmtDate(f.date)}</span>
            </div>
            {f.summary && <p className="text-sm font-medium text-foreground">{f.summary}</p>}
            {f.details && <p className="text-sm text-slate-600 mt-1 leading-relaxed">{f.details}</p>}
            {f.teacher_name && <p className="text-xs text-slate-400 mt-1">— {f.teacher_name}</p>}
          </div>
        ))}
      </div>
    );
  }
  if (key === "report_card") {
    const rows = s.report_card || [];
    if (rows.length === 0) return <Empty>No report cards generated yet.</Empty>;
    return (
      <Table
        headers={["Period", "Date", "Status"]}
        rows={rows.map((r) => [r.period_label || "—", fmtDate(r.created_at || r.date), r.status || "—"])}
      />
    );
  }
  if (key === "guardians") {
    const rows = s.guardians || [];
    if (rows.length === 0) return <Empty>No guardian info on file.</Empty>;
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {rows.map((g, i) => (
          <div key={g.id || g.email || g.phone || `g-${i}`} className={`border rounded-2xl p-4 ${g.is_me ? "border-[#2563EB] bg-[#EFF6FF]" : "border-slate-200"}`}>
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-full overflow-hidden bg-[#DBEAFE] flex items-center justify-center shrink-0">
                {g.photo
                  ? <img src={g.photo} alt="" className="w-full h-full object-cover" />
                  : <Users className="w-5 h-5 text-[#2563EB]" />}
              </span>
              <div className="min-w-0">
                <p className="font-heading font-semibold flex items-center gap-1.5">
                  {g.name || "Guardian"}
                  {g.is_me && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#2563EB] text-white">You</span>}
                </p>
                <p className="text-xs text-slate-500 truncate">{g.email || "—"}</p>
                <p className="text-xs text-slate-500">{g.phone || "—"}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }
  return null;
}

const Field = ({ label, value, className = "" }) => (
  <div className={className}>
    <dt className="text-xs uppercase font-heading text-slate-400">{label}</dt>
    <dd className="text-sm font-medium text-foreground">{value || "—"}</dd>
  </div>
);

const Stat = ({ label, value, tint }) => (
  <div className={`rounded-2xl px-4 py-3 ${tint}`}>
    <p className="text-xs uppercase font-heading opacity-80">{label}</p>
    <p className="text-xl font-heading font-bold">{value}</p>
  </div>
);

const Empty = ({ children }) => (
  <p className="text-sm text-slate-500 text-center py-6">{children}</p>
);

const Table = ({ headers, rows }) => (
  <div className="overflow-x-auto rounded-2xl border border-slate-200">
    <table className="w-full text-sm">
      <thead className="bg-slate-50">
        <tr>{headers.map((h) => <th key={h} className="text-left px-4 py-2 font-heading text-xs uppercase text-slate-500">{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={`row-${i}-${r[0] ?? ""}`} className="border-t border-slate-100">
            {r.map((c, j) => <td key={`${i}-${j}`} className="px-4 py-2.5">{c}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
